import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Tenant } from '../../identity/api';
import { Button } from '@nyce/admin-kit/ui';
import { activeTarget, client, useLogin, tenantPath, identityPath } from '../../identity/session';
import { fmtDate } from '../../identity/format';
import { Code, ErrorBox, Loading, Page, Tabs, useAsync, useNotice } from '../../components/common';
import { StatusChip } from './Tenants';
import { TenantFields, toUpdate, useTenantForm, type TenantFormValue } from './tenant/TenantForm';
import { CopyTenantDialog } from './tenant/CopyTenantDialog';
import { ClaimsTab } from './tenant/ClaimsTab';
import { RolesTab } from './tenant/RolesTab';
import { ServiceKeysTab } from './tenant/ServiceKeysTab';
import { ClientsTab } from './tenant/ClientsTab';
import { UsersTab } from './tenant/UsersTab';
import { SsoTab } from './tenant/SsoTab';
import { AliasesEditor } from './Aliases';

type Tab = 'general' | 'claims' | 'roles' | 'keys' | 'clients' | 'users' | 'sso' | 'aliases';
const TABS: { value: Tab; label: string }[] = [
  { value: 'general', label: 'general' },
  { value: 'claims', label: 'claims' },
  { value: 'roles', label: 'roles' },
  { value: 'keys', label: 'service keys' },
  { value: 'clients', label: 'clients' },
  { value: 'users', label: 'users' },
  { value: 'sso', label: 'single sign-on' },
  { value: 'aliases', label: 'aliases' },
];

const toForm = (t: Tenant): TenantFormValue => ({
  name: t.name,
  status: t.status,
  identityProvider: t.identityProvider,
  identityProviderUrl: t.identityProviderUrl,
  customerId: t.customerId,
});

function GeneralTab({
  tenant,
  canWrite,
  onSaved,
}: {
  tenant: Tenant;
  canWrite: boolean;
  onSaved: (t: Tenant) => void;
}) {
  const form = useTenantForm(toForm(tenant));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    form.setValue(toForm(tenant));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);
  const dirty = JSON.stringify(toUpdate(form.value)) !== JSON.stringify(toUpdate(toForm(tenant)));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await client.tenants.update(tenant.id, toUpdate(form.value)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="nyk-card">
      <div className="nyk-card-head">
        <h2>General</h2>
        {canWrite && (
          <Button size="sm" onClick={() => void save()} loading={busy} disabled={!form.valid || !dirty}>
            Save
          </Button>
        )}
      </div>
      <ErrorBox error={error} />
      <TenantFields value={form.value} onChange={form.setValue} withStatus disabled={!canWrite} />
      <table className="nyk-kv" style={{ marginTop: 16 }}>
        <tbody>
          <tr>
            <th>Id</th>
            <td>
              <Code>{tenant.id}</Code>
            </td>
          </tr>
          <tr>
            <th>Created</th>
            <td>{fmtDate(tenant.createdAt)}</td>
          </tr>
          <tr>
            <th>Updated</th>
            <td>{fmtDate(tenant.updatedAt)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const login = useLogin();
  const canWrite = !!login?.access.canWrite;
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((t) => t.value === params.get('tab')) ? params.get('tab') : 'general') as Tab;
  const tenant = useAsync(() => client.tenants.get(id), [id]);
  const toast = useNotice();
  const navigate = useNavigate();
  const [copying, setCopying] = useState(false);
  const t = tenant.data;
  return (
    <Page
      title={
        <>
          <Link to={identityPath('/identity/tenants')} className="nyk-back">
            Tenants
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          {t?.name ?? '…'}
        </>
      }
      description={
        t && (
          <span className="nyk-inline">
            <StatusChip status={t.status} />
            <span>
              {t.customerName ?? t.customerId} · {t.identityProvider}
            </span>
            <Code>{t.id}</Code>
          </span>
        )
      }
      actions={
        t &&
        canWrite && (
          <Button variant="ghost" onClick={() => setCopying(true)}>
            New as copy
          </Button>
        )
      }
    >
      <ErrorBox error={tenant.error} />
      {toast.element}
      <Loading loading={tenant.loading && !t} />
      {t && (
        <>
          <Tabs
            value={tab}
            onChange={(v) =>
              setParams(v === 'general' ? { target: activeTarget() } : { tab: v, target: activeTarget() })
            }
            options={TABS}
          />
          {tab === 'general' && (
            <GeneralTab
              tenant={t}
              canWrite={canWrite}
              onSaved={(saved) => {
                tenant.setData(saved);
                toast.success(`Saved ${saved.name}`);
              }}
            />
          )}
          {tab === 'claims' && (
            <ClaimsTab tenant={t} canWrite={canWrite} onChanged={() => tenant.reload()} notify={toast} />
          )}
          {tab === 'roles' && (
            <RolesTab tenant={t} canWrite={canWrite} onChanged={() => tenant.reload()} notify={toast} />
          )}
          {tab === 'keys' && <ServiceKeysTab tenant={t} canWrite={canWrite} notify={toast} />}
          {tab === 'clients' && <ClientsTab tenant={t} canWrite={canWrite} notify={toast} />}
          {tab === 'users' && <UsersTab tenant={t} canWrite={canWrite} notify={toast} />}
          {tab === 'sso' && <SsoTab tenant={t} canWrite={canWrite} notify={toast} />}
          {tab === 'aliases' && (
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Aliases</h2>
                <span className="nyk-ui-hint">ids other systems use for this installation</span>
              </div>
              <AliasesEditor
                level="tenant"
                aliases={t.aliases ?? []}
                canWrite={canWrite}
                replace={async (next) => {
                  await client.tenants.aliases.replace(t.id, next);
                  await tenant.reload();
                }}
                notify={toast}
              />
            </div>
          )}
          {copying && (
            <CopyTenantDialog
              source={t}
              onCreated={(created, warning) => {
                if (warning) toast.error(warning);
                else toast.success(`Created ${created.name} as a copy of ${t.name}`);
                navigate(tenantPath(created.id));
              }}
              onClose={() => setCopying(false)}
            />
          )}
        </>
      )}
    </Page>
  );
}
